import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, PhoneIncoming, RefreshCw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, errMsg, fmtTime } from "@/lib/api";
import { usePanel } from "./PanelContext";

export default function CallerHub() {
  const { api, biz, reload, openNewOrder } = usePanel();
  const [calls, setCalls] = useState([]);
  const [phone, setPhone] = useState("");
  const appUrl = `${window.location.origin}/caller`;
  const load = () => api.get("/calls").then((r) => setCalls(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Kopyalandı"); };
  const regen = async () => { if (window.confirm("Yeni kod oluşturulsun mu? Eski kodla giriş yapılmış telefonların tekrar giriş yapması gerekir.")) { await api.post("/business/regenerate-caller-code"); reload(); } };
  const simulate = async () => {
    try { await api.post("/calls", { phone }); setPhone(""); load(); toast.success("Arama kaydedildi"); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6 rise" data-testid="callerhub-page">
      <div><h1 className="text-3xl font-extrabold">Caller ID</h1><p className="text-sm text-slate-500">Arayan müşterinin bilgileri panelde anında açılır. Aramadan tek tıkla sipariş oluşturun.</p></div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-3xl bg-[#FF6B0A] text-white p-6 space-y-3">
          <p className="font-display font-bold text-lg">Caller ID Uygulaması</p>
          <p className="text-sm text-white/85">Sipariş telefonuna APK dosyasını indirip kurun, izinleri verin ve aşağıdaki bağlantı kodunu girin. Telefon çaldığında numara <b>otomatik</b> panele düşer ve müşteri bilgisi açılır.</p>
          <a href={`${API}/apk/gazi-sepet-callerid.apk`} download="gazi-sepet-callerid.apk" type="application/vnd.android.package-archive" className="inline-flex items-center gap-2 rounded-full bg-[#0F1F45] hover:bg-[#1a2f63] px-4 py-2 text-sm font-semibold" data-testid="caller-apk-download">Caller ID APK İndir (Android)</a>
          <div className="flex items-center gap-2 bg-white/15 rounded-xl p-2 text-sm">
            <span className="flex-1 truncate font-mono-num" data-testid="caller-app-url">{appUrl}</span>
            <Button size="icon" variant="ghost" className="h-8 w-8 text-white" onClick={() => copy(appUrl)} data-testid="caller-app-url-copy"><Copy className="h-4 w-4" /></Button>
            <a href="/caller" target="_blank" rel="noreferrer" data-testid="caller-app-open"><ExternalLink className="h-4 w-4" /></a>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm">Bağlantı kodu:</span>
            <span className="font-mono-num text-2xl font-black tracking-widest" data-testid="caller-code">{biz.caller_code}</span>
            <Button size="icon" variant="ghost" className="text-white" onClick={regen} data-testid="caller-code-regen-btn"><RefreshCw className="h-4 w-4" /></Button>
          </div>
        </div>
        <div className="rounded-3xl bg-white border p-6 space-y-3">
          <p className="font-display font-bold">Otomatik numara aktarımı (gelişmiş)</p>
          <p className="text-sm text-slate-600">Android'de MacroDroid / Tasker gibi bir otomasyon uygulamasıyla “gelen arama” olayında aşağıdaki adrese istek gönderirseniz numara otomatik düşer:</p>
          <pre className="text-xs bg-slate-900 text-slate-100 rounded-xl p-3 overflow-x-auto" data-testid="caller-webhook-info">{`POST ${API}/caller/call
X-Caller-Code: ${biz.caller_code}
Content-Type: application/json
{"phone": "<arayan numara>"}`}</pre>
          <div className="flex gap-2 pt-2">
            <Input placeholder="Numara girerek arama ekle" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="callerhub-phone-input" />
            <Button onClick={simulate} className="rounded-full bg-[#0F1F45]" data-testid="callerhub-add-call-btn">Arama Ekle</Button>
          </div>
        </div>
      </div>
      <div className="rounded-3xl bg-white border p-6">
        <h3 className="font-display font-bold mb-3">Son Aramalar</h3>
        <div className="divide-y">
          {calls.map((c) => (
            <div key={c.id} className="py-3 flex flex-wrap items-center gap-3" data-testid={`call-row-${c.id}`}>
              <PhoneIncoming className="h-4 w-4 text-[#FF6B0A]" />
              <span className="font-mono-num font-semibold">{c.phone}</span>
              <span className="text-sm">{c.name || "Yeni müşteri"}</span>
              <span className="text-xs text-slate-500 truncate max-w-xs">{c.address}</span>
              <span className="ml-auto text-xs text-slate-500">{fmtTime(c.at)}</span>
              <Button size="sm" className="rounded-full bg-[#FF6B0A] hover:bg-[#E55A00]" onClick={() => openNewOrder({ customer_phone: c.phone, customer_name: c.name, address: c.address })} data-testid={`call-order-btn-${c.id}`}>Sipariş Oluştur</Button>
            </div>
          ))}
          {!calls.length && <p className="text-sm text-slate-500 py-3">Henüz arama yok.</p>}
        </div>
      </div>
    </div>
  );
}
